/**
 * ShareLinksTab Component (Story 14.2)
 *
 * Renders the client-portal tab content: list of share links for a client,
 * generation button, and per-link actions (copy/regenerate/edit/revoke).
 *
 * - Plan-limit-aware "Generate" button (disabled at limit with upgrade hint)
 * - Empty/loading/error states
 * - TanStack Query for data fetching with invalidation on mutation
 */

import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "@phosphor-icons/react";
import { Button } from "@provenleap/ui";
import { motion } from "motion/react";
import {
  listPortalLinks,
  type PortalLinkProjection,
  type PortalLinkWithToken,
} from "../../lib/portalLinksApi";
import { ShareLinkCard } from "./ShareLinkCard";
import { CreateShareLinkSheet } from "./CreateShareLinkSheet";
import { EditShareLinkSheet } from "./EditShareLinkSheet";
import { TokenRevealDialog } from "./TokenRevealDialog";
import { RegenerateTokenDialog } from "./RegenerateTokenDialog";
import { RevokeLinkDialog } from "./RevokeLinkDialog";
import { usePermission, Actions } from "../../hooks/usePermission";
import { usePlanLimits } from "../../hooks/usePlanLimits";
import {
  isUnlimited,
  isAtLimit,
  PLAN_HIERARCHY,
  PLAN_DISPLAY_NAMES,
  type Plan,
} from "@provenleap/types";

export interface ShareLinksTabProps {
  clientId: string;
  clientName: string;
  showToast: (type: "success" | "error" | "info", message: string) => void;
}

function nextTier(current: Plan): Plan | null {
  const i = PLAN_HIERARCHY.indexOf(current);
  if (i < 0 || i === PLAN_HIERARCHY.length - 1) return null;
  return PLAN_HIERARCHY[i + 1] ?? null;
}

export function ShareLinksTab({
  clientId,
  clientName,
  showToast,
}: ShareLinksTabProps) {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermission();
  const { plan, limits } = usePlanLimits();
  const canManage = hasPermission(Actions.MANAGE_REPORTS);

  const { data: links, isLoading, error, refetch } = useQuery<
    PortalLinkProjection[]
  >({
    queryKey: ["portalLinks", clientId],
    queryFn: () => listPortalLinks(clientId),
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [editLink, setEditLink] = useState<PortalLinkProjection | null>(null);
  // Keep the previously-edited link in state for ~250ms after `editLink` clears
  // so the modal exit animation can finish before unmounting.
  const [lastEditLink, setLastEditLink] = useState<PortalLinkProjection | null>(
    null
  );
  useEffect(() => {
    if (editLink) {
      setLastEditLink(editLink);
      return;
    }
    if (!lastEditLink) return;
    const t = setTimeout(() => setLastEditLink(null), 300);
    return () => clearTimeout(t);
  }, [editLink, lastEditLink]);
  const [tokenReveal, setTokenReveal] = useState<{
    open: boolean;
    link: PortalLinkWithToken | null;
    title: string;
  }>({ open: false, link: null, title: "Share link created" });
  const [regenerateLink, setRegenerateLink] =
    useState<PortalLinkProjection | null>(null);
  const [revokeLink, setRevokeLink] =
    useState<PortalLinkProjection | null>(null);

  const activeCount = (links ?? []).filter((l) => l.status !== "revoked").length;
  const portalLimit = limits.portalLinkLimit;
  const atLimit = isAtLimit(activeCount, portalLimit);
  const upgradeTier = atLimit && !isUnlimited(portalLimit) ? nextTier(plan) : null;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["portalLinks", clientId] });
  };

  function handleCreated(created: PortalLinkWithToken) {
    setCreateOpen(false);
    setTokenReveal({ open: true, link: created, title: "Share link created" });
    invalidate();
  }

  function handleRegenerated(regenerated: PortalLinkWithToken) {
    setRegenerateLink(null);
    setTokenReveal({
      open: true,
      link: regenerated,
      title: "New URL — old URL is now invalid",
    });
    invalidate();
  }

  return (
    <div className="mt-8" data-testid="share-links-tab">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        className="flex items-start justify-between gap-4 mb-6"
      >
        <div>
          <h2 className="text-xl font-semibold">Client Portal Share Links</h2>
          <p className="text-[13px] text-muted-foreground mt-1 max-w-prose">
            Generate a shareable link so {clientName} can view their dashboard
            or specific reports — no login required. You control expiration,
            password, and what data is visible.
          </p>
        </div>
        {canManage && (
          <div className="flex flex-col items-end gap-1">
            <Button
              variant="brand"
              className="rounded-2xl text-[13px] cursor-pointer"
              onClick={() => setCreateOpen(true)}
              disabled={atLimit}
              title={
                atLimit && upgradeTier
                  ? `Upgrade to ${PLAN_DISPLAY_NAMES[upgradeTier]} for more share links`
                  : atLimit
                    ? "Plan limit reached"
                    : undefined
              }
              data-testid="create-share-link-button"
            >
              <Plus size={16} className="mr-1" />
              Generate Share Link
            </Button>
            {atLimit && upgradeTier && (
              <p className="text-[12px] text-muted-foreground">
                Plan limit reached. Upgrade to {PLAN_DISPLAY_NAMES[upgradeTier]} for more.
              </p>
            )}
          </div>
        )}
      </motion.div>

      {isLoading && (
        <div className="space-y-3" data-testid="share-links-loading">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-24 bg-muted animate-pulse rounded-[22px]"
            />
          ))}
        </div>
      )}

      {error && !isLoading && (
        <div
          className="rounded-[22px] border-[0.5px] border-destructive bg-destructive/5 p-6 text-sm text-destructive"
          role="alert"
          data-testid="share-links-error"
        >
          <p className="mb-3">Failed to load share links: {error.message}</p>
          <Button
            variant="outline"
            className="rounded-2xl text-[13px] cursor-pointer"
            onClick={() => refetch()}
          >
            Try Again
          </Button>
        </div>
      )}

      {!isLoading && !error && (links ?? []).length === 0 && (
        <div
          className="rounded-[22px] border-[0.5px] border-input bg-background p-12 text-center"
          style={{ border: "0.5px solid var(--hairline)" }}
          data-testid="share-links-empty"
        >
          <h3 className="text-[15px] font-semibold mb-2">
            No share links yet
          </h3>
          <p className="text-[13px] text-muted-foreground max-w-md mx-auto">
            Create a share link to let {clientName} view this dashboard
            without creating an account.
          </p>
        </div>
      )}

      {!isLoading && !error && (links ?? []).length > 0 && (
        <div className="space-y-3" data-testid="share-links-list">
          {links!.map((link) => (
            <ShareLinkCard
              key={link.id}
              link={link}
              canManage={canManage}
              onEdit={() => setEditLink(link)}
              onRegenerate={() => setRegenerateLink(link)}
              onRevoke={() => setRevokeLink(link)}
            />
          ))}
        </div>
      )}

      {/* Always-mounted so AnimatePresence inside can run the close (exit)
          animation. Conditional `{createOpen && ...}` would unmount the entire
          tree the moment `open=false`, killing the fade/scale exit. */}
      <CreateShareLinkSheet
        clientId={clientId}
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={handleCreated}
        showToast={showToast}
      />

      {/* Edit dialog needs a non-null `link` prop, so we hold the most recent
          one in `lastEditLink` and only clear it after the exit animation has a
          chance to play (driven by `editOpen`). */}
      {lastEditLink && (
        <EditShareLinkSheet
          link={lastEditLink}
          open={!!editLink}
          onOpenChange={(o) => !o && setEditLink(null)}
          onSaved={() => {
            setEditLink(null);
            invalidate();
            showToast("success", "Share link updated");
          }}
          showToast={showToast}
        />
      )}

      {tokenReveal.open && tokenReveal.link && (
        <TokenRevealDialog
          open={tokenReveal.open}
          title={tokenReveal.title}
          link={tokenReveal.link}
          onClose={() => setTokenReveal({ open: false, link: null, title: "" })}
          showToast={showToast}
        />
      )}

      {regenerateLink && (
        <RegenerateTokenDialog
          link={regenerateLink}
          open={!!regenerateLink}
          onOpenChange={(o) => !o && setRegenerateLink(null)}
          onRegenerated={handleRegenerated}
          showToast={showToast}
        />
      )}

      {revokeLink && (
        <RevokeLinkDialog
          link={revokeLink}
          open={!!revokeLink}
          onOpenChange={(o) => !o && setRevokeLink(null)}
          onRevoked={() => {
            setRevokeLink(null);
            invalidate();
            showToast("success", "Share link revoked");
          }}
          showToast={showToast}
        />
      )}
    </div>
  );
}
